import { devLogger } from "@certa/common/utils/devLogger";
import type { CoreComponent } from "../coreComponents/coreComponents.types";
import { getDragContext } from "../interactionEngine/dragContext";
import type { SCNodeWithMetaData } from "../renderingEngine/renderManager";
import { getInstances } from "../renderingEngine/renderer";

export const calculateLayout = ({
  rootNodeKey,
  modifiedNodeKeys,
  keyMap
}: {
  rootNodeKey: string;
  modifiedNodeKeys: string[];
  keyMap: Map<string, SCNodeWithMetaData>;
}) => {
  const instances = getInstances();
  const rootCoreComponent = instances[rootNodeKey];
  if (!rootCoreComponent) {
    throw Error(`Component not found for key: ${rootCoreComponent}`);
  }
  rootCoreComponent.yogaNode.calculateLayout();

  const dragContext = getDragContext();
  // Do a bottom-up traversal and stop when previous and new values of nodes
  // are same, and/or you've reached the root node.

  const filteredModifiedNodes = dragContext.node
    ? modifiedNodeKeys.filter(key => {
        const instance = instances[key];
        if (!instance) {
          throw Error(`Component not found for key: ${key}`);
        }
        return dragContext.node
          ? instance.getZIndex() < dragContext.node.getZIndex()
          : true;
      })
    : modifiedNodeKeys;

  function getParentKey(key: string) {
    const scnodeWithmetaData = keyMap.get(key);
    const parentKey = scnodeWithmetaData?.parentKey;
    return parentKey ?? null;
  }

  // Create a bottom-up tree
  const allParentKeys = new Set<string>();
  const bottomUpTree: { [key: string]: string[] } = {};
  let rootKey: string | null = null;

  if (filteredModifiedNodes.length === 0) {
    return new Set([rootNodeKey]);
  }

  filteredModifiedNodes.forEach(key => {
    let currentKey: string | null = key;
    while (currentKey !== null) {
      allParentKeys.add(currentKey);
      const parentKey = getParentKey(currentKey);
      if (parentKey) {
        if (!bottomUpTree[parentKey]) {
          bottomUpTree[parentKey] = [];
        }
        bottomUpTree[parentKey].push(currentKey);
        // If the parent is already in the tree, we can stop traversing
        if (
          bottomUpTree[parentKey].length > 1 ||
          allParentKeys.has(parentKey)
        ) {
          break;
        }
      } else {
        rootKey = currentKey; // This is the root node
        break; // We've reached the root, so we can stop
      }
      currentKey = parentKey;
    }
  });

  if (!rootKey) {
    // TODO: There's an edge case where the root node is not assigned
    // at all. RCA pending, but if above logic runs, without ever
    // reading the rootKey = currentKey, then this is possible.
    devLogger.log({
      rootNodeKey,
      allParentKeys,
      bottomUpTree
    });
    throw Error("Root node not found");
  }

  // Function to traverse the tree top-down and find the topmost dirty nodes
  function findTopmostDirtyNodes(key: string): Set<string> {
    const instance = instances[key];
    if (!instance) {
      throw Error(`Component not found for key: ${key}`);
    }

    if (key !== rootKey && isDirty(instance)) {
      return new Set([key]);
    }

    const dirtyNodes = new Set<string>();
    if (bottomUpTree[key]) {
      for (const childKey of bottomUpTree[key]) {
        const childDirtyNodes = findTopmostDirtyNodes(childKey);
        childDirtyNodes.forEach(node => dirtyNodes.add(node));
      }
    }

    return dirtyNodes;
  }

  // Find the topmost dirty nodes starting from the root
  const topLevelDirtyNodes = findTopmostDirtyNodes(rootKey);

  if (topLevelDirtyNodes.has(rootKey)) {
    // If root is dirty, update everything
    devLogger.log("Root node is dirty, updating entire tree");
  } else if (topLevelDirtyNodes.size > 0) {
    // If non-root dirty nodes are found, log them
    devLogger.log(
      `Topmost dirty nodes: ${Array.from(topLevelDirtyNodes).join(", ")}`
    );
  } else {
    devLogger.log("No dirty nodes found");
  }

  return topLevelDirtyNodes;
};

const isDirty = (instance: CoreComponent) => {
  const lastLayoutValues = instance.getLayoutValues();
  const nextLayoutValues = instance.yogaNode.getComputedLayout();
  const isDirty =
    lastLayoutValues.left !== nextLayoutValues.left ||
    lastLayoutValues.top !== nextLayoutValues.top ||
    lastLayoutValues.width !== nextLayoutValues.width ||
    lastLayoutValues.height !== nextLayoutValues.height;
  return isDirty;
};
